/**
 * ContactMemberModal.jsx — Living-member "Contact Me" workflow.
 *
 * Any visitor (signed-in or anonymous) can send a private message to
 * the tree admins asking to reconnect with a member. The admin/owner
 * receives an email + inbox entry and replies via IFT messaging —
 * the member's email and phone are NEVER revealed publicly.
 *
 * Backed by `POST /api/family-trees/{treeId}/members/{memberId}/contact`.
 */
import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Loader2, ShieldCheck } from "lucide-react";

const SUBJECTS = [
  { v: "add_me",      label: "Please add me to this family tree" },
  { v: "need_access", label: "Requesting access to view the full profile" },
  { v: "share_info",  label: "I have information / photos to share" },
  { v: "correction",  label: "Correction or update needed" },
  { v: "other",       label: "General enquiry" },
];

export default function ContactMemberModal({ treeId, memberId, memberName, onClose }) {
  const [yourName,  setYourName]  = useState("");
  const [yourEmail, setYourEmail] = useState("");
  const [subject,   setSubject]   = useState("other");
  const [message,   setMessage]   = useState("");
  const [busy, setBusy] = useState(false);
  // Privacy notice is dismissible per-device — once the user has read
  // "email and phone are never revealed" they don't need it re-shown
  // every time they open the Contact modal. Lives in localStorage so
  // it survives reloads. Reset by clearing site data.
  const NOTICE_KEY = "ift_contact_member_notice_dismissed_v1";
  const [showNotice, setShowNotice] = useState(() => {
    if (typeof window === "undefined") return true;
    try { return window.localStorage.getItem(NOTICE_KEY) !== "1"; }
    catch { return true; }
  });
  const dismissNotice = () => {
    setShowNotice(false);
    try { window.localStorage.setItem(NOTICE_KEY, "1"); } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!yourName.trim() || !yourEmail.trim() || !message.trim()) {
      toast.error("Please fill your name, email and message.");
      return;
    }
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/members/${memberId}/contact`, {
        your_name: yourName.trim(),
        your_email: yourEmail.trim(),
        subject,
        message: message.trim(),
      });
      toast.success("Your message was sent to the family admins.");
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="contact-member-modal">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="rounded-2xl w-full max-w-md my-auto p-6 space-y-4 shadow-xl"
        style={{ background: "var(--wofy-paper)" }}
      >
        <header className="flex items-start justify-between">
          <div>
            <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>CONTACT ME</div>
            <h2 className="font-display text-xl">Reconnect with {memberName || "this person"}</h2>
          </div>
          <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100" data-testid="contact-member-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="text-xs flex items-start gap-2 rounded-lg p-3" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          {showNotice ? (
            <span className="flex-1">
              Their email and phone are never revealed. A family admin reviews your request and replies through IndianFamilyTree messaging.
              {" "}
              <button
                type="button"
                onClick={dismissNotice}
                className="underline font-semibold opacity-70 hover:opacity-100"
                data-testid="contact-member-dismiss-notice"
              >
                Got it — don't show again
              </button>
            </span>
          ) : (
            <span className="flex-1 opacity-70">
              Private message · admin-reviewed.
            </span>
          )}
        </div>

        <input
          value={yourName}
          onChange={(e) => setYourName(e.target.value)}
          placeholder="Your full name"
          maxLength={120}
          required
          className="w-full rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "var(--wofy-line)" }}
          data-testid="contact-member-name"
        />
        <input
          type="email"
          value={yourEmail}
          onChange={(e) => setYourEmail(e.target.value)}
          placeholder="Your email"
          required
          className="w-full rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "var(--wofy-line)" }}
          data-testid="contact-member-email"
        />
        <select
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "var(--wofy-line)" }}
          data-testid="contact-member-subject"
        >
          {SUBJECTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Introduce yourself and share how you know them…"
          rows={5}
          maxLength={4000}
          required
          className="w-full rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "var(--wofy-line)" }}
          data-testid="contact-member-message"
        />

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm" data-testid="contact-member-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="contact-member-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {busy ? "Sending…" : "Send message"}
          </button>
        </div>
      </form>
    </div>
  );
}
